import { useState } from 'react';
import { WATCH_ZONE_PRESETS } from '../lib/constants';
import { activeCouncilNames } from '../config/registry';

const COUNCIL_ORDER = activeCouncilNames();

const ZONE_DOTS = ["#c0a060", "#c66b3d", "#4f8fe8", "#5aba6a", "#9b7be8"];

export default function WatchZoneManager({
  zones,
  addZone,
  deleteZone,
  updateZoneRadius,
  zApps,
  theme,
  setSelZone,
  zonesOnlyMode,
  setZonesOnlyMode,
  selectedZone,
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [zoneErr, setZoneErr] = useState("");
  const [selLoc, setSelLoc] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [openCouncil, setOpenCouncil] = useState(null);
  const [addRadius, setAddRadius] = useState(5000);
  const [editMode, setEditMode] = useState(false);

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #263248",
    background: "#0f1726",
    color: "#e8e0d8",
    fontSize: "var(--text-sm)",
    fontFamily: "inherit",
    boxSizing: "border-box",
    outline: "none",
  };

  const handleAdd = async (event) => {
    event.preventDefault();
    if (!selLoc) {
      setZoneErr("Please select a location first.");
      return;
    }
    const added = await addZone({ name: selLoc.name, selLoc, radius: addRadius });
    if (added === "limit") {
      setZoneErr("Free Standard can save up to 1 watch zone.");
      return;
    }
    if (added === false) {
      setZoneErr("Watch zone already exists.");
      return;
    }
    if (added === "error") {
      setZoneErr("Couldn't save — check connection and retry.");
      return;
    }
    setZoneErr("");
    setSelLoc(null);
    setAddRadius(5000);
    setShowDropdown(false);
    setOpenCouncil(null);
    setShowAdd(false);
  };

  const resetAdd = () => {
    setShowAdd(false);
    setZoneErr("");
    setSelLoc(null);
    setAddRadius(5000);
    setShowDropdown(false);
    setOpenCouncil(null);
  };

  const toggleEdit = () => {
    setEditMode((current) => {
      const next = !current;
      if (!next) resetAdd();
      return next;
    });
  };

  return (
    <div
      style={{
        background: "linear-gradient(180deg, #0a1726 0%, #07111d 100%)",
        borderTop: "1px solid rgba(192,160,96,0.18)",
        padding: "10px 10px 12px",
        maxHeight: editMode || showAdd ? "48vh" : "36vh",
        overflowY: "auto",
        boxShadow: "0 -18px 42px rgba(0,0,0,0.36)",
      }}
    >
      <div style={{ width: 38, height: 4, borderRadius: 2, background: "#536071", margin: "0 auto 12px", opacity: 0.75 }} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: "#f3efe7", fontSize: 16, fontWeight: 850, letterSpacing: 0 }}>Saved areas</div>
          <div style={{ color: "#8a95a8", fontSize: 12, marginTop: 2 }}>
            {zones.length} watch zone{zones.length !== 1 ? "s" : ""} · {zonesOnlyMode ? "zones only" : "regional map"}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {zones.length > 0 && (
            <button
              onClick={() => setZonesOnlyMode((current) => !current)}
              type="button"
              style={{
                minHeight: 32,
                padding: "0 10px",
                borderRadius: 999,
                border: `1px solid ${zonesOnlyMode ? "#c0a060" : "#263248"}`,
                background: zonesOnlyMode ? "rgba(192,160,96,0.14)" : "#101827",
                color: zonesOnlyMode ? "#c0a060" : "#9aa4b2",
                fontSize: 12,
                fontWeight: 800,
                cursor: "pointer",
                fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
            >
              Zones
            </button>
          )}
          <button
            onClick={toggleEdit}
            type="button"
            style={{
              minHeight: 32,
              padding: "0 4px",
              border: "none",
              background: "transparent",
              color: "#c0a060",
              fontSize: 13,
              fontWeight: 850,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {editMode ? "Done" : "Edit"}
          </button>
        </div>
      </div>

      {editMode && (
        <div style={{ display: "flex", gap: 8, marginBottom: showAdd ? 10 : 8 }}>
          <button
            onClick={() => {
              if (showAdd) resetAdd();
              else setShowAdd(true);
            }}
            type="button"
            style={{
              width: "100%",
              minHeight: 38,
              borderRadius: 8,
              border: `1px solid ${showAdd ? "#263248" : "#c0a060"}`,
              background: showAdd ? "#101827" : "rgba(192,160,96,0.13)",
              color: showAdd ? "#9aa4b2" : "#c0a060",
              fontSize: 13,
              fontWeight: 850,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {showAdd ? "Cancel new area" : "+ Add saved area"}
          </button>
        </div>
      )}

      {showAdd && (
        <form
          onSubmit={handleAdd}
          style={{
            marginBottom: 10,
            padding: 10,
            background: "#0d1624",
            borderRadius: 8,
            border: "1px solid #243044",
          }}
        >
          <div style={{ position: "relative", marginBottom: 10 }}>
            <button
              type="button"
              onClick={() => setShowDropdown((current) => !current)}
              style={{
                ...inputStyle,
                marginBottom: 0,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                minHeight: 42,
                textAlign: "left",
              }}
            >
              {selLoc ? <span>{selLoc.name}</span> : <span style={{ color: "#7f8999" }}>Select a location...</span>}
              <span style={{ marginLeft: "auto", color: "#8a95a8", fontSize: 12, paddingLeft: 8, flexShrink: 0 }}>
                {showDropdown ? "up" : "down"}
              </span>
            </button>
            {showDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  right: 0,
                  background: "#0d1624",
                  border: "1px solid #c0a060",
                  borderRadius: 8,
                  maxHeight: 260,
                  overflowY: "auto",
                  zIndex: 300,
                  boxShadow: "0 12px 32px rgba(0,0,0,0.46)",
                }}
              >
                {COUNCIL_ORDER.map((group) => {
                  const locations = WATCH_ZONE_PRESETS.filter((preset) => preset.council === group);
                  const isOpen = openCouncil === group;
                  return (
                    <div key={group}>
                      <button
                        type="button"
                        onClick={() => setOpenCouncil(isOpen ? null : group)}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 14px",
                          background: isOpen ? "rgba(192,160,96,0.12)" : "#0d1624",
                          border: 0,
                          borderBottom: "1px solid #243044",
                          color: "#c0a060",
                          cursor: "pointer",
                          fontFamily: "inherit",
                          textAlign: "left",
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 800 }}>{group.replace(" Council", "")}</span>
                        <span style={{ color: "#8a95a8", fontSize: 12 }}>{locations.length}</span>
                      </button>
                      {isOpen && locations.map((preset) => (
                        <button
                          key={preset.name}
                          type="button"
                          onClick={() => {
                            setSelLoc({ name: preset.name, lat: preset.lat, lng: preset.lng });
                            setShowDropdown(false);
                          }}
                          style={{
                            width: "100%",
                            padding: "11px 14px 11px 22px",
                            color: "#f0f2f6",
                            background: "transparent",
                            border: 0,
                            borderBottom: "1px solid rgba(36,48,68,0.62)",
                            cursor: "pointer",
                            fontSize: 13,
                            fontFamily: "inherit",
                            textAlign: "left",
                          }}
                        >
                          {preset.name}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: zoneErr ? 6 : 10 }}>
            {[2000, 5000, 10000].map((radius) => (
              <button
                key={radius}
                type="button"
                onClick={() => setAddRadius(radius)}
                style={{
                  minHeight: 34,
                  borderRadius: 7,
                  border: radius === addRadius ? "1px solid #c0a060" : "1px solid #263248",
                  background: radius === addRadius ? "rgba(192,160,96,0.16)" : "transparent",
                  color: radius === addRadius ? "#c0a060" : "#9aa4b2",
                  fontSize: 13,
                  fontWeight: radius === addRadius ? 850 : 650,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {radius / 1000}km
              </button>
            ))}
          </div>
          {zoneErr && <div style={{ fontSize: 12, color: theme.danger || "#ef6f6c", marginBottom: 8 }}>{zoneErr}</div>}
          <button
            type="submit"
            style={{
              width: "100%",
              minHeight: 42,
              borderRadius: 8,
              border: "none",
              background: selLoc ? "#c0a060" : "rgba(192,160,96,0.36)",
              color: "#07111d",
              fontSize: 14,
              fontWeight: 900,
              cursor: selLoc ? "pointer" : "default",
              fontFamily: "inherit",
            }}
          >
            {selLoc ? "Add saved area" : "Select a location first"}
          </button>
        </form>
      )}

      <div style={{ display: "grid", gap: 8 }}>
        {zones.length === 0 && (
          <div
            style={{
              border: "1px dashed #263248",
              borderRadius: 8,
              padding: 12,
              color: "#8a95a8",
              fontSize: 13,
              textAlign: "center",
            }}
          >
            Add a saved area to focus the map and DA alerts.
          </div>
        )}

        {zones.map((zone, index) => {
          const count = zApps(zone).length;
          const selected = selectedZone?.id === zone.id;
          const dotColor = zone.color || ZONE_DOTS[index % ZONE_DOTS.length];

          return (
            <div
              key={zone.id}
              style={{
                border: `1px solid ${selected ? "#c0a060" : "#243044"}`,
                background: selected ? "rgba(192,160,96,0.10)" : "#101827",
                borderRadius: 8,
                overflow: "hidden",
                boxShadow: selected ? "inset 0 0 0 1px rgba(192,160,96,0.16)" : "none",
              }}
            >
              <button
                type="button"
                onClick={() => setSelZone(selected ? null : zone)}
                style={{
                  width: "100%",
                  minHeight: 50,
                  display: "grid",
                  gridTemplateColumns: "auto minmax(0, 1fr) auto auto",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  border: 0,
                  background: "transparent",
                  color: "#f0f2f6",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: dotColor,
                    boxShadow: selected ? `0 0 0 4px ${dotColor}24` : "none",
                  }}
                />
                <span style={{ minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      color: selected ? "#fff7dd" : "#f0f2f6",
                      fontSize: 14,
                      fontWeight: 850,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {zone.name}
                  </span>
                </span>
                <span style={{ color: "#8a95a8", fontSize: 12, whiteSpace: "nowrap" }}>
                  {count} DA{count !== 1 ? "s" : ""} · {Math.round(zone.radius / 1000)}km
                </span>
                <span style={{ color: selected ? "#c0a060" : "#6f7b8f", fontSize: 22, lineHeight: 1 }}>›</span>
              </button>

              {editMode && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 8,
                    alignItems: "center",
                    padding: "0 10px 10px 34px",
                  }}
                >
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                    {[2000, 5000, 10000].map((radius) => (
                      <button
                        key={radius}
                        type="button"
                        onClick={() => updateZoneRadius(zone.id, radius)}
                        style={{
                          minHeight: 30,
                          borderRadius: 7,
                          border: radius === zone.radius ? "1px solid #c0a060" : "1px solid #263248",
                          background: radius === zone.radius ? "rgba(192,160,96,0.16)" : "transparent",
                          color: radius === zone.radius ? "#c0a060" : "#9aa4b2",
                          fontSize: 12,
                          fontWeight: radius === zone.radius ? 850 : 650,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        {radius / 1000}km
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => deleteZone(zone.id)}
                    aria-label={`Remove ${zone.name}`}
                    style={{
                      minHeight: 30,
                      padding: "0 10px",
                      borderRadius: 7,
                      border: "1px solid rgba(239,111,108,0.34)",
                      background: "rgba(239,111,108,0.10)",
                      color: "#ef6f6c",
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
